import { useId, useState } from "react";
import type { JSX } from "react";
import { toLocalDate } from "../../domain/time";
import type { LocalAttribution } from "../../offline/model";
import { useLocalAttributions, useOffline, useProjectCache } from "../../offline/OfflineContext";
import { toAttributionRequest } from "../../offline/SyncEngine";
import { useI18n } from "../../shared/i18n/I18nContext";
import { useOwnerKey } from "../../shared/session/tenantSelection";
import { Alert } from "../../shared/ui/Alert";
import { cx } from "../../shared/ui/classNames";
import { formatDateTime, formatLocalDate, formatTime } from "../../shared/ui/format";
import styles from "../../shared/ui/ui.module.css";
import { AttributionRequestForm } from "./AttributionRequestForm";
import { projectLabelOf } from "./model/pastDays";

/** Properties of {@link RejectedAttributions}. */
export interface RejectedAttributionsProps {
  /** Time zone of the displayed times. */
  readonly timeZone: string;
}

/**
 * Project attributions the server rejected after synchronization (spec 8.1.4 "Konflikt klären",
 * F-07). An attribution refused because its project period is locked (follow-up
 * `AttributionRequest`) can be turned into a request with the same identifier, so that an approval
 * stores exactly this attribution; every rejected attribution can be dismissed. Renders nothing
 * without rejected attributions.
 *
 * @param props - Component properties.
 * @returns The section or nothing.
 */
export function RejectedAttributions(props: RejectedAttributionsProps): JSX.Element | null {
  const { t, languageTag } = useI18n();
  const { store } = useOffline();
  const owner = useOwnerKey();
  const attributions = useLocalAttributions(owner);
  const cache = useProjectCache(owner);
  const headingId = useId();
  const [requesting, setRequesting] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const rejected = attributions.filter((attribution) => attribution.syncStatus === "rejected");

  if (rejected.length === 0 && message === null) {
    return null;
  }

  const projects = cache?.myProjects.projects ?? [];
  const label = (projectId: string | null): string =>
    projectId === null
      ? t("project.none")
      : (projectLabelOf(projects, projectId) ?? t("dayOverview.unknownProject"));
  const describe = (attribution: LocalAttribution): string => {
    const start = attribution.occurredAt ?? attribution.rangeStart ?? 0;
    const text =
      attribution.rangeEnd === null
        ? t("rejectedAttributions.switch", {
            time: formatDateTime(start, languageTag, props.timeZone),
            project: label(attribution.projectId),
          })
        : t("rejectedAttributions.range", {
            date: formatLocalDate(toLocalDate(start, props.timeZone), languageTag),
            from: formatTime(start, languageTag, props.timeZone),
            to: formatTime(attribution.rangeEnd, languageTag, props.timeZone),
            project: label(attribution.projectId),
          });

    return text;
  };

  const section = (
    <section className={styles["stack"]} aria-labelledby={headingId}>
      <h2 id={headingId}>{t("rejectedAttributions.title")}</h2>
      {message === null ? null : <Alert tone="success">{message}</Alert>}
      <ul className={styles["stack"]}>
        {rejected.map((attribution) => (
          <li key={attribution.id} className={cx(styles["card"], styles["stack"])}>
            <p>{describe(attribution)}</p>
            <p>
              {attribution.followUp === "AttributionRequest"
                ? t("rejectedAttributions.locked")
                : t("rejectedAttributions.reason", { code: attribution.error?.code ?? "?" })}
            </p>
            <div className={styles["row"]}>
              {attribution.followUp === "AttributionRequest" ? (
                <button
                  type="button"
                  className={styles["button"]}
                  aria-expanded={requesting === attribution.id}
                  onClick={() => {
                    setMessage(null);
                    setRequesting(requesting === attribution.id ? null : attribution.id);
                  }}
                >
                  {t("dayOverview.request")}
                </button>
              ) : null}
              <button
                type="button"
                className={styles["button"]}
                onClick={() => {
                  void store.dismissRejectedAttribution(attribution.id);
                }}
              >
                {t("rejectedAttributions.dismiss")}
              </button>
            </div>
            {requesting === attribution.id ? (
              <AttributionRequestForm
                subject={describe(attribution)}
                projectChoice={null}
                initialProjectId={attribution.projectId}
                fixedProjectLabel={label(attribution.projectId)}
                buildAttribution={() => toAttributionRequest(attribution)}
                onDone={() => {
                  // Another request may be open by now (F-63); only this one closes.
                  setRequesting((current) => (current === attribution.id ? null : current));
                  setMessage(t("attributionRequest.sent"));
                  void store.dismissRejectedAttribution(attribution.id);
                }}
                onCancel={() => {
                  setRequesting(null);
                }}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );

  return section;
}
